import { BillingCatalog } from "@/data/models/BillingCatalog";

/**
 * How a PDE's `erpBoQVersion` was matched against the client's billing catalogs.
 *
 * A PDE names the LV to use via the free-text `erpBoQVersion` field, written by
 * X360. That string is matched against `BillingCatalog.version`, which is **not
 * unique**: `aqua_AQGS2602` and `aqua_bro_AQGS2602_BRO` both carry
 * `version: "AQGS2602"`. Article numbering is similar across LVs, so a wrong
 * match silently shows plausible but incorrect item texts — worse than showing
 * none. The distinct statuses exist so the UI can say which case occurred.
 */
export enum BillingCatalogMatch {
  /** `erpBoQVersion` was the catalog's document id. The unambiguous path. */
  BY_ID = "byId",
  /** Exactly one active catalog carried this `version`. */
  BY_VERSION = "byVersion",
  /** Several active catalogs carried this `version`; one was chosen deterministically. */
  AMBIGUOUS = "ambiguous",
  /** No active catalog matched. */
  NOT_FOUND = "notFound",
}

/** Outcome of resolving a PDE's `erpBoQVersion` to a billing catalog. */
export interface BillingCatalogResolution {
  /** The chosen catalog, or null when nothing matched. */
  catalog: BillingCatalog | null;
  match: BillingCatalogMatch;
  /** How many catalogs matched. Only meaningful for the version fallback. */
  matchCount: number;
}

/**
 * True when a string is safe to pass to Firestore's `.doc()`.
 *
 * Both halves of a PDE's billing reference are free text controlled by X360, and a
 * document id may not contain `/`. This matters in practice: the öSV LV publishes its
 * own canonical id as `D/öSV/Dienstleister/WS/1.0/12.2024`. Without this guard such a
 * value makes `.doc()` throw, and the caller loses the item texts entirely instead of
 * falling back. Used for the catalog id (`erpBoQVersion`) and for the article number
 * (`billingItemId`), which is the item's document id.
 */
export function isUsableFirestoreDocumentId(value: string): boolean {
  return (
    value.length > 0 &&
    value.length <= 1500 &&
    !value.includes("/") &&
    value !== "." &&
    value !== ".."
  );
}

/**
 * Matches a PDE's `erpBoQVersion` against catalogs by their `version` field.
 *
 * Used as the fallback after an exact document-id lookup fails. When several
 * catalogs share the same `version`, the one with the lowest document id is
 * chosen so the result is stable across calls, and the caller is told the match
 * was ambiguous. The previous `find()` returned whichever catalog Firestore
 * happened to list first, which could silently swap Broenen and standard Aqua
 * texts between page loads.
 *
 * Only `active` catalogs are considered, matching the id-based path.
 *
 * @param catalogs - Candidate catalogs, typically all active ones for the client.
 * @param erpBoQVersion - The PDE's free-text LV identifier.
 */
export function resolveBillingCatalogByVersion(
  catalogs: BillingCatalog[],
  erpBoQVersion: string
): BillingCatalogResolution {
  const matches = catalogs.filter(
    (catalog) => catalog.status === "active" && catalog.version === erpBoQVersion
  );

  if (matches.length === 0) {
    return { catalog: null, match: BillingCatalogMatch.NOT_FOUND, matchCount: 0 };
  }

  if (matches.length === 1) {
    return { catalog: matches[0], match: BillingCatalogMatch.BY_VERSION, matchCount: 1 };
  }

  const [chosen] = [...matches].sort((a, b) => a.id.localeCompare(b.id));
  return {
    catalog: chosen,
    match: BillingCatalogMatch.AMBIGUOUS,
    matchCount: matches.length,
  };
}
